import React, { useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Input,
    NumberInput,
    NumberInputField,
    VStack,
    HStack,
    useToast,
    FormErrorMessage,
    useColorModeValue,
} from '@chakra-ui/react';
import { trainerService, CreateTrainerData } from '../../../../services/trainerService';

interface CreateTrainerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onTrainerCreated: () => void;
}

const CreateTrainerModal: React.FC<CreateTrainerModalProps> = ({
    isOpen,
    onClose,
    onTrainerCreated,
}) => {
    const [formData, setFormData] = useState<CreateTrainerData>({
        name: '',
        email: '',
        password: '',
        phone: '',
        specialization: '',
        certification: '',
        experienceYears: 0,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<Partial<Record<keyof CreateTrainerData, string>>>({});
    const toast = useToast();

    // Color mode values
    const modalBg = useColorModeValue('white', 'navy.800');
    const textColor = useColorModeValue('navy.700', 'white');
    const inputBg = useColorModeValue('white', 'navy.700');
    const inputBorder = useColorModeValue('gray.200', 'gray.600');

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            password: '',
            phone: '',
            specialization: '',
            certification: '',
            experienceYears: 0,
        });
        setErrors({});
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof CreateTrainerData, string>> = {};

        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
        if (!formData.password.trim()) newErrors.password = 'Password is required';
        else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
        if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
        if (!formData.specialization.trim()) newErrors.specialization = 'Specialization is required';
        if (!formData.certification.trim()) newErrors.certification = 'Certification is required';
        if (formData.experienceYears < 0) newErrors.experienceYears = 'Experience years must be positive';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        try {
            await trainerService.createTrainer(formData);

            toast({
                title: 'Trainer Created',
                description: `Trainer ${formData.name} created successfully`,
                status: 'success',
                duration: 5000,
                isClosable: true,
            });

            resetForm();
            onTrainerCreated();
            onClose();
        } catch (error: any) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to create trainer',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (field: keyof CreateTrainerData, value: string | number) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="xl">
            <ModalOverlay />
            <ModalContent bg={modalBg}>
                <ModalHeader color={textColor}>Create New Trainer</ModalHeader>
                <ModalCloseButton color={textColor} />
                <form onSubmit={handleSubmit}>
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <HStack spacing={4}>
                                <FormControl isInvalid={!!errors.name} flex={1}>
                                    <FormLabel color={textColor}>Full Name</FormLabel>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        placeholder="Enter trainer's full name"
                                        bg={inputBg}
                                        borderColor={inputBorder}
                                        color={textColor}
                                        _focus={{ borderColor: 'blue.500', bg: inputBg }}
                                    />
                                    <FormErrorMessage>{errors.name}</FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={!!errors.email} flex={1}>
                                    <FormLabel color={textColor}>Email</FormLabel>
                                    <Input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleChange('email', e.target.value)}
                                        placeholder="Enter email address"
                                        bg={inputBg}
                                        borderColor={inputBorder}
                                        color={textColor}
                                        _focus={{ borderColor: 'blue.500', bg: inputBg }}
                                    />
                                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                                </FormControl>
                            </HStack>

                            <HStack spacing={4}>
                                <FormControl isInvalid={!!errors.password} flex={1}>
                                    <FormLabel color={textColor}>Password</FormLabel>
                                    <Input
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => handleChange('password', e.target.value)}
                                        placeholder="Enter password"
                                        bg={inputBg}
                                        borderColor={inputBorder}
                                        color={textColor}
                                        _focus={{ borderColor: 'blue.500', bg: inputBg }}
                                    />
                                    <FormErrorMessage>{errors.password}</FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={!!errors.phone} flex={1}>
                                    <FormLabel color={textColor}>Phone</FormLabel>
                                    <Input
                                        value={formData.phone}
                                        onChange={(e) => handleChange('phone', e.target.value)}
                                        placeholder="Enter phone number"
                                        bg={inputBg}
                                        borderColor={inputBorder}
                                        color={textColor}
                                        _focus={{ borderColor: 'blue.500', bg: inputBg }}
                                    />
                                    <FormErrorMessage>{errors.phone}</FormErrorMessage>
                                </FormControl>
                            </HStack>

                            <HStack spacing={4}>
                                <FormControl isInvalid={!!errors.specialization} flex={1}>
                                    <FormLabel color={textColor}>Specialization</FormLabel>
                                    <Input
                                        value={formData.specialization}
                                        onChange={(e) => handleChange('specialization', e.target.value)}
                                        placeholder="e.g., Weight Training, Cardio, Yoga"
                                        bg={inputBg}
                                        borderColor={inputBorder}
                                        color={textColor}
                                        _focus={{ borderColor: 'blue.500', bg: inputBg }}
                                    />
                                    <FormErrorMessage>{errors.specialization}</FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={!!errors.certification} flex={1}>
                                    <FormLabel color={textColor}>Certification</FormLabel>
                                    <Input
                                        value={formData.certification}
                                        onChange={(e) => handleChange('certification', e.target.value)}
                                        placeholder="e.g., NASM-CPT, ACSM-CPT"
                                        bg={inputBg}
                                        borderColor={inputBorder}
                                        color={textColor}
                                        _focus={{ borderColor: 'blue.500', bg: inputBg }}
                                    />
                                    <FormErrorMessage>{errors.certification}</FormErrorMessage>
                                </FormControl>
                            </HStack>

                            <FormControl isInvalid={!!errors.experienceYears}>
                                <FormLabel color={textColor}>Years of Experience</FormLabel>
                                <NumberInput
                                    value={formData.experienceYears}
                                    onChange={(_, value) => handleChange('experienceYears', value)}
                                    min={0}
                                    max={50}
                                >
                                    <NumberInputField 
                                        placeholder="Enter years of experience" 
                                        bg={inputBg}
                                        borderColor={inputBorder}
                                        color={textColor}
                                        _focus={{ borderColor: 'blue.500', bg: inputBg }}
                                    />
                                </NumberInput>
                                <FormErrorMessage>{errors.experienceYears}</FormErrorMessage>
                            </FormControl>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button mr={3} onClick={handleClose} variant="ghost" color={textColor}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            colorScheme="blue"
                            isLoading={isLoading}
                            loadingText="Creating..."
                        >
                            Create Trainer
                        </Button>
                    </ModalFooter>
                </form>
            </ModalContent>
        </Modal>
    );
};

export default CreateTrainerModal;
